var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import React, { useCallback, useEffect, useState } from "react";
import { faArrowDown, faArrowUp, faEye, faEyeSlash, faGripVertical, faUndo } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Modal from "../atoms/Modal";
/**
 * Get field label from definitions or generate from key
 */
var getFieldLabel = function (key, fieldDefinitions) {
    if (fieldDefinitions) {
        var definition = fieldDefinitions.find(function (def) { return def.key === key; });
        if (definition) {
            return definition.label;
        }
    }
    // Fallback: convert camelCase to Title Case
    return key.replace(/([A-Z])/g, " $1").trim();
};
/**
 * Get field visibility default from definitions
 */
var getFieldVisibility = function (key, fieldDefinitions) {
    if (fieldDefinitions) {
        var definition = fieldDefinitions.find(function (def) { return def.key === key; });
        if (definition) {
            return definition.visible;
        }
    }
    // Default to visible if no definition
    return true;
};
/**
 * Get field order from definitions
 */
var getFieldOrder = function (key, fieldDefinitions, fallbackIndex) {
    if (fallbackIndex === void 0) { fallbackIndex = 0; }
    if (fieldDefinitions) {
        var definition = fieldDefinitions.find(function (def) { return def.key === key; });
        if (definition) {
            return definition.order;
        }
    }
    // Fallback to provided index
    return fallbackIndex;
};
export var ExportFieldCustomizer = function (_a) {
    var isOpen = _a.isOpen, onClose = _a.onClose, availableFields = _a.availableFields, storageKey = _a.storageKey, onSave = _a.onSave, _b = _a.sampleData, sampleData = _b === void 0 ? [] : _b, fieldDefinitions = _a.fieldDefinitions, configVersion = _a.configVersion;
    var _c = useState([]), fields = _c[0], setFields = _c[1];
    var _d = useState(null), draggedIndex = _d[0], setDraggedIndex = _d[1];
    var _e = useState(0), currentSampleIndex = _e[0], setCurrentSampleIndex = _e[1];
    // Load saved configuration from localStorage
    useEffect(function () {
        if (isOpen && availableFields.length > 0) {
            var versionKey = "".concat(storageKey, "-version");
            var savedConfig = localStorage.getItem(storageKey);
            var savedVersion = localStorage.getItem(versionKey);
            var initialFields = void 0;
            // Check if we should use cached config or reset to defaults
            var shouldUseCachedConfig = savedConfig && (!configVersion || savedVersion === String(configVersion));
            if (shouldUseCachedConfig) {
                try {
                    var parsed = JSON.parse(savedConfig);
                    // Merge with available fields to handle new fields
                    var fieldMap_1 = new Map(parsed.map(function (f) { return [f.key, f]; }));
                    initialFields = availableFields.map(function (key, index) {
                        var existing = fieldMap_1.get(key);
                        return (existing || {
                            key: key,
                            label: getFieldLabel(key, fieldDefinitions),
                            visible: getFieldVisibility(key, fieldDefinitions),
                            order: getFieldOrder(key, fieldDefinitions, index),
                        });
                    });
                }
                catch (_a) {
                    // If parsing fails, create default configuration from field definitions
                    initialFields = availableFields.map(function (key, index) { return ({
                        key: key,
                        label: getFieldLabel(key, fieldDefinitions),
                        visible: getFieldVisibility(key, fieldDefinitions),
                        order: getFieldOrder(key, fieldDefinitions, index),
                    }); });
                }
            }
            else {
                // Version mismatch or no cached config - use defaults from field definitions
                if (savedConfig && configVersion) {
                    // Clear old configuration and version to ensure clean state
                    localStorage.removeItem(storageKey);
                    localStorage.removeItem(versionKey);
                }
                initialFields = availableFields.map(function (key, index) { return ({
                    key: key,
                    label: getFieldLabel(key, fieldDefinitions),
                    visible: getFieldVisibility(key, fieldDefinitions),
                    order: getFieldOrder(key, fieldDefinitions, index),
                }); });
            }
            // Sort by order
            initialFields.sort(function (a, b) { return a.order - b.order; });
            setFields(initialFields);
        }
    }, [isOpen, availableFields, storageKey, fieldDefinitions, configVersion]);
    var handleToggleVisibility = useCallback(function (index) {
        setFields(function (prev) { return prev.map(function (field, i) { return (i === index ? __assign(__assign({}, field), { visible: !field.visible }) : field); }); });
    }, []);
    var handleMoveUp = useCallback(function (index) {
        if (index === 0)
            return;
        setFields(function (prev) {
            var _a;
            var newFields = __spreadArray([], prev, true);
            _a = [newFields[index], newFields[index - 1]], newFields[index - 1] = _a[0], newFields[index] = _a[1];
            return newFields.map(function (field, i) { return (__assign(__assign({}, field), { order: i })); });
        });
    }, []);
    var handleMoveDown = useCallback(function (index) {
        setFields(function (prev) {
            var _a;
            if (index === prev.length - 1)
                return prev;
            var newFields = __spreadArray([], prev, true);
            _a = [newFields[index + 1], newFields[index]], newFields[index] = _a[0], newFields[index + 1] = _a[1];
            return newFields.map(function (field, i) { return (__assign(__assign({}, field), { order: i })); });
        });
    }, []);
    var handleReset = useCallback(function () {
        var defaultFields = availableFields.map(function (key, index) { return ({
            key: key,
            label: getFieldLabel(key, fieldDefinitions),
            visible: getFieldVisibility(key, fieldDefinitions),
            order: getFieldOrder(key, fieldDefinitions, index),
        }); });
        // Sort by order from definitions
        defaultFields.sort(function (a, b) { return a.order - b.order; });
        setFields(defaultFields);
    }, [availableFields, fieldDefinitions]);
    var handleSave = useCallback(function () {
        // Save to localStorage
        localStorage.setItem(storageKey, JSON.stringify(fields));
        // Save version if provided
        if (configVersion !== undefined) {
            localStorage.setItem("".concat(storageKey, "-version"), String(configVersion));
        }
        // Call parent callback
        onSave(fields);
        onClose();
    }, [fields, storageKey, onSave, onClose, configVersion]);
    var handleDragStart = useCallback(function (e, index) {
        setDraggedIndex(index);
        e.dataTransfer.effectAllowed = "move";
    }, []);
    var handleDragOver = useCallback(function (e) {
        e.preventDefault();
        e.dataTransfer.dropEffect = "move";
    }, []);
    var handleDrop = useCallback(function (e, dropIndex) {
        e.preventDefault();
        if (draggedIndex === null || draggedIndex === dropIndex)
            return;
        setFields(function (prev) {
            var newFields = __spreadArray([], prev, true);
            var draggedField = newFields[draggedIndex];
            newFields.splice(draggedIndex, 1);
            newFields.splice(dropIndex, 0, draggedField);
            return newFields.map(function (field, i) { return (__assign(__assign({}, field), { order: i })); });
        });
        setDraggedIndex(null);
    }, [draggedIndex]);
    var visibleCount = fields.filter(function (f) { return f.visible; }).length;
    var hasSampleData = sampleData.length > 0;
    var currentSample = hasSampleData ? sampleData[currentSampleIndex] : null;
    var handlePreviousSample = function () {
        setCurrentSampleIndex(function (prev) { return (prev > 0 ? prev - 1 : sampleData.length - 1); });
    };
    var handleNextSample = function () {
        setCurrentSampleIndex(function (prev) { return (prev < sampleData.length - 1 ? prev + 1 : 0); });
    };
    var formatSampleValue = function (value) {
        if (value === null || value === undefined)
            return "—";
        if (typeof value === "string" && value.includes("T") && value.includes("Z")) {
            // Format dates
            try {
                return new Date(value).toLocaleString();
            }
            catch (_a) {
                return value;
            }
        }
        if (typeof value === "boolean")
            return value ? "Yes" : "No";
        if (typeof value === "number")
            return value.toLocaleString();
        return String(value);
    };
    return (React.createElement(Modal, { isOpen: isOpen, onClose: onClose, title: "Customize Excel Export Fields", maxWidth: hasSampleData ? "800px" : "600px" },
        React.createElement("div", { style: { padding: "24px" } },
            React.createElement("div", { style: { marginBottom: "24px" } },
                React.createElement("p", { style: { margin: "0 0 16px 0", color: "#666", fontSize: "14px" } }, "Select which fields to include in your Excel export and arrange them in your preferred order. Drag and drop to reorder, or use the arrow buttons."),
                React.createElement("div", { style: {
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        padding: "12px 16px",
                        backgroundColor: "#f8f9fa",
                        borderRadius: "6px",
                        fontSize: "14px",
                    } },
                    React.createElement("span", null,
                        React.createElement("strong", null, visibleCount),
                        " of ",
                        React.createElement("strong", null, fields.length),
                        " fields selected"),
                    React.createElement("button", { onClick: handleReset, style: {
                            display: "flex",
                            alignItems: "center",
                            gap: "6px",
                            padding: "6px 12px",
                            backgroundColor: "transparent",
                            border: "1px solid #ccc",
                            borderRadius: "4px",
                            cursor: "pointer",
                            fontSize: "12px",
                            color: "#666",
                        } },
                        React.createElement(FontAwesomeIcon, { icon: faUndo }),
                        "Reset to Default"))),
            hasSampleData && (React.createElement("div", { style: { marginBottom: "16px" } },
                React.createElement("div", { style: {
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        padding: "12px 16px",
                        backgroundColor: "#f0f8ff",
                        borderRadius: "6px",
                        border: "1px solid #d0e7ff",
                    } },
                    React.createElement("span", { style: { fontSize: "14px", fontWeight: "500", color: "#333" } }, "Sample Data Preview"),
                    React.createElement("div", { style: { display: "flex", alignItems: "center", gap: "8px" } },
                        React.createElement("button", { onClick: handlePreviousSample, style: {
                                padding: "4px 8px",
                                backgroundColor: "transparent",
                                border: "1px solid #ccc",
                                borderRadius: "3px",
                                cursor: "pointer",
                                fontSize: "12px",
                            }, title: "Previous interaction" }, "\u2190"),
                        React.createElement("span", { style: { fontSize: "12px", color: "#666" } },
                            currentSampleIndex + 1,
                            " of ",
                            sampleData.length),
                        React.createElement("button", { onClick: handleNextSample, style: {
                                padding: "4px 8px",
                                backgroundColor: "transparent",
                                border: "1px solid #ccc",
                                borderRadius: "3px",
                                cursor: "pointer",
                                fontSize: "12px",
                            }, title: "Next interaction" }, "\u2192"))))),
            React.createElement("div", { style: {
                    maxHeight: "400px",
                    overflowY: "auto",
                    border: "1px solid #e0e0e0",
                    borderRadius: "6px",
                } }, fields.map(function (field, index) { return (React.createElement("div", { key: field.key, draggable: true, onDragStart: function (e) { return handleDragStart(e, index); }, onDragOver: handleDragOver, onDrop: function (e) { return handleDrop(e, index); }, style: {
                    display: "flex",
                    alignItems: "center",
                    padding: "12px 16px",
                    borderBottom: index < fields.length - 1 ? "1px solid #e0e0e0" : "none",
                    backgroundColor: field.visible ? "#fff" : "#f8f9fa",
                    cursor: "move",
                    opacity: draggedIndex === index ? 0.5 : 1,
                    transition: "all 0.2s ease",
                } },
                React.createElement(FontAwesomeIcon, { icon: faGripVertical, style: {
                        color: "#ccc",
                        marginRight: "12px",
                        cursor: "grab",
                    } }),
                React.createElement("button", { onClick: function () { return handleToggleVisibility(index); }, style: {
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        marginRight: "12px",
                        color: field.visible ? "#28a745" : "#ccc",
                        fontSize: "16px",
                    }, title: field.visible ? "Hide field" : "Show field" },
                    React.createElement(FontAwesomeIcon, { icon: field.visible ? faEyeSlash : faEye })),
                React.createElement("div", { style: { flex: 1, fontSize: "14px" } },
                    React.createElement("div", { style: {
                            fontWeight: "500",
                            color: field.visible ? "#333" : "#999",
                        } }, field.label)),
                hasSampleData && (React.createElement("div", { style: {
                        width: "200px",
                        marginLeft: "16px",
                        marginRight: "16px",
                        fontSize: "13px",
                        color: "#666",
                        backgroundColor: "#f8f9fa",
                        padding: "8px 12px",
                        borderRadius: "4px",
                        border: "1px solid #e0e0e0",
                        wordBreak: "break-word",
                        fontFamily: "monospace",
                    } }, currentSample ? formatSampleValue(currentSample[field.key]) : "—")),
                React.createElement("div", { style: { display: "flex", gap: "4px" } },
                    React.createElement("button", { onClick: function () { return handleMoveUp(index); }, disabled: index === 0, style: {
                            background: "none",
                            border: "1px solid #ccc",
                            borderRadius: "3px",
                            cursor: index === 0 ? "not-allowed" : "pointer",
                            padding: "4px 6px",
                            color: index === 0 ? "#ccc" : "#666",
                            fontSize: "12px",
                        }, title: "Move up" },
                        React.createElement(FontAwesomeIcon, { icon: faArrowUp })),
                    React.createElement("button", { onClick: function () { return handleMoveDown(index); }, disabled: index === fields.length - 1, style: {
                            background: "none",
                            border: "1px solid #ccc",
                            borderRadius: "3px",
                            cursor: index === fields.length - 1 ? "not-allowed" : "pointer",
                            padding: "4px 6px",
                            color: index === fields.length - 1 ? "#ccc" : "#666",
                            fontSize: "12px",
                        }, title: "Move down" },
                        React.createElement(FontAwesomeIcon, { icon: faArrowDown }))))); })),
            React.createElement("div", { style: {
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: "12px",
                    marginTop: "24px",
                    paddingTop: "16px",
                    borderTop: "1px solid #e0e0e0",
                } },
                React.createElement("button", { onClick: onClose, style: {
                        padding: "10px 20px",
                        backgroundColor: "transparent",
                        border: "1px solid #ccc",
                        borderRadius: "4px",
                        cursor: "pointer",
                        fontSize: "14px",
                        color: "#666",
                    } }, "Cancel"),
                React.createElement("button", { onClick: handleSave, style: {
                        padding: "10px 20px",
                        backgroundColor: "#374B94",
                        border: "none",
                        borderRadius: "4px",
                        cursor: "pointer",
                        fontSize: "14px",
                        color: "white",
                        fontWeight: "500",
                    } }, "Save Configuration")))));
};
export default ExportFieldCustomizer;
